import React, { useEffect, useState, useRef } from "react";
import { FaLocationArrow, FaSearchLocation, FaSpinner, FaExclamationTriangle } from "react-icons/fa";
import { useTranslation } from 'react-i18next';
import { useToast } from '../../hooks/ToastContext';
import { CustomInputBox1 } from '../../Templates';
import "../../GoogleMapsRoute.css";

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const EventLocationMap = ({ onLocationSelect }) => {
  const { t } = useTranslation('common');
  const { addToast } = useToast();
  const mapRef = useRef(null);
  const locationInputRef = useRef(null);
  
  const [map, setMap] = useState(null);
  const [marker, setMarker] = useState(null);
  const [locationValue, setLocationValue] = useState("");
  const [isMapLoading, setIsMapLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mapInitialized, setMapInitialized] = useState(false);

  // First, check if Google Maps API is available
  useEffect(() => {
    const checkGoogleMapsLoaded = () => {
      if (window.google && window.google.maps) {
        setIsMapLoading(false);
      } else {
        console.warn("Google Maps API not loaded yet, waiting...");
        setTimeout(checkGoogleMapsLoaded, 500);
      }
    };

    checkGoogleMapsLoaded();
  }, []);

  // Initialize the map after the component has fully mounted and API is loaded
  useEffect(() => {
    if (isMapLoading || mapInitialized || !mapRef.current) return;

    // Add a small delay to ensure DOM is fully rendered
    const initTimer = setTimeout(() => {
      try {
        console.log("Initializing Google Maps for event location...");

        const defaultLocation = { lat: 56.9677, lng: 24.1056 }; // Centered on Latvia
        
        // Initialize map
        const newMap = new window.google.maps.Map(mapRef.current, {
          center: defaultLocation,
          zoom: 8,
          fullscreenControl: false,
          mapTypeControl: true,
          streetViewControl: false,
        });

        // Create a marker
        const newMarker = new window.google.maps.Marker({
          position: defaultLocation,
          map: newMap,
          draggable: true,
          title: t('events.event_location')
        });

        // Add marker drag event
        newMarker.addListener('dragend', () => {
          const position = newMarker.getPosition();
          const geocoder = new window.google.maps.Geocoder();
          
          geocoder.geocode({ location: { lat: position.lat(), lng: position.lng() } }, (results, status) => {
            if (status === 'OK' && results[0]) {
              const address = results[0].formatted_address;
              setLocationValue(address);
              onLocationSelect("address_to", results[0]);
            }
          });
        });

        setMap(newMap);
        setMarker(newMarker);
        setMapInitialized(true);
        console.log("Map initialized successfully for event location");
      } catch (e) {
        console.error("Error initializing map:", e);
        setError(t('errors.map_initialization_failed') || "Failed to initialize map");
      }
    }, 100); // Short delay to ensure DOM is ready
    
    return () => clearTimeout(initTimer);
  }, [isMapLoading, mapInitialized, t]);

  // Setup autocomplete after map is initialized
  useEffect(() => {
    if (!mapInitialized || !map || !locationInputRef.current) return;

    try {
      console.log("Setting up autocomplete for event location...");
      const defaultBounds = new window.google.maps.LatLngBounds(
        new window.google.maps.LatLng(55, 23),
        new window.google.maps.LatLng(58, 26)
      );

      // Autocomplete options
      const autocompleteOptions = {
        bounds: defaultBounds,
        strictBounds: false,
        componentRestrictions: { country: "lv" },
      };

      // Setup autocomplete for location input
      const locationAutoComplete = new window.google.maps.places.Autocomplete(
        locationInputRef.current.querySelector("input"),
        autocompleteOptions
      );

      locationAutoComplete.addListener("place_changed", () => {
        const place = locationAutoComplete.getPlace();
        if (place.geometry) {
          setLocationValue(place.formatted_address);
          
          // Update marker position
          marker.setPosition(place.geometry.location);
          map.setCenter(place.geometry.location);
          map.setZoom(15);
          
          // Pass the place data to parent component
          onLocationSelect("address_to", place);
        }
      });
      
      console.log("Autocomplete setup complete for event location");
    } catch (e) {
      console.error("Error setting up autocomplete:", e);
      setError(t('errors.autocomplete_setup_failed') || "Failed to set up address search");
    }
  }, [mapInitialized, map, marker, t, onLocationSelect]);

  if (isMapLoading) {
    return (
      <div className="map-loading">
        <FaSpinner className="spinner" />
        <p>{t('app.loading') || "Loading..."}</p>
      </div>
    );
  }

  return (
    <div className="travel-route-container event-location-map">
      {error && (
        <div className="route-error-message">
          <FaExclamationTriangle />
          <span>{error}</span>
        </div>
      )}
      
      <div className="travel-route-inputs">
        <div className="input-group location-input" ref={locationInputRef}>
          <div className="input-icon">
            <FaSearchLocation />
          </div>
          <CustomInputBox1
            placeholder={t('events.event_location_placeholder') || "Event location"}
            value={locationValue}
            onChange={(e) => setLocationValue(e.target.value)}
          />
        </div>
        <p className="location-hint">{t('events.location_map_help')}</p>
      </div>

      <div 
        className="map-box"
        ref={mapRef}
      />
    </div>
  );
};

export default EventLocationMap;